import {
    Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Table, TableBody, TableCell,
    TableHead, TableRow, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MDButton from '@components/MDButton';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { formatCnpj } from '@functions/brazil-documents';
import { formatPhone } from '@functions/format-phone';
import { ELeadOutcome } from '../../kanban-board/services/responses/lead.response';
import { CompanyService } from '../services/company.service';
import { CompanyDetailResponse } from '../services/responses/company.response';

const OUTCOME_KEY: Record<ELeadOutcome, string> = {
    [ELeadOutcome.Open]: 'desfecho.emAberto',
    [ELeadOutcome.Won]: 'desfecho.ganho',
    [ELeadOutcome.Lost]: 'desfecho.perdido',
};

const money = (value?: number | null) =>
    value == null ? '-' : value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface Props {
    companyId: string | null;
    onClose: () => void;
    onEdit: (id: string) => void;
}

/// A empresa, as pessoas ligadas e os leads delas, só os que quem abre enxerga (regra do quadro).
export const CompanyDetailModal = ({ companyId, onClose, onEdit }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [detail, setDetail] = useState<CompanyDetailResponse | null>(null);

    useEffect(() => {
        setDetail(null);
        if (!companyId) return;

        new CompanyService()
            .detail(companyId)
            .then((res) => setDetail(res.data.data))
            .catch((err) => {
                showAlert(apiErrorMessage(err), 'error', 3000);
                onClose();
            });
    }, [companyId]);

    const company = detail?.company;

    return (
        <Dialog open={!!companyId} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
            <DialogTitle>{company?.name ?? t('companies.detalhe')}</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2 }}>
                {!detail ? (
                    <Skeleton variant="rounded" height={240} />
                ) : (
                    <>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            {company.tradeName && (
                                <Typography variant="body2">{t('companies.nomeFantasia')}: {company.tradeName}</Typography>
                            )}
                            {company.cnpj && (
                                <Typography variant="body2">{t('companies.cnpj')}: {formatCnpj(company.cnpj)}</Typography>
                            )}
                            {company.phone && (
                                <Typography variant="body2">{t('people.telefone')}: {formatPhone(company.phone)}</Typography>
                            )}
                            {company.email && (
                                <Typography variant="body2">{t('people.email')}: {company.email}</Typography>
                            )}
                        </Box>

                        <Box>
                            <Typography variant="h6">{t('companies.pessoas')}</Typography>
                            {detail.people.length === 0 ? (
                                <Typography variant="body2">{t('companies.semPessoas')}</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('general.nome')}</TableCell>
                                            <TableCell>{t('people.telefone')}</TableCell>
                                            <TableCell>{t('people.email')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.people.map((person) => (
                                            <TableRow key={person.id}>
                                                <TableCell>{person.name}</TableCell>
                                                <TableCell>{person.phone ? formatPhone(person.phone) : '-'}</TableCell>
                                                <TableCell>{person.email || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>

                        <Box>
                            <Typography variant="h6">{t('companies.leads')}</Typography>
                            {detail.leads.length === 0 ? (
                                <Typography variant="body2">{t('companies.semLeads')}</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('crmLista.lead')}</TableCell>
                                            <TableCell>{t('crmLista.etapa')}</TableCell>
                                            <TableCell>{t('crmLista.status')}</TableCell>
                                            <TableCell>{t('crmLista.responsavel')}</TableCell>
                                            <TableCell>{t('crmLista.valor')}</TableCell>
                                            <TableCell>{t('exportar.desfecho')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.leads.map((lead) => (
                                            <TableRow
                                                key={lead.id}
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => navigate(`/crm/lead/${lead.id}`)}
                                            >
                                                <TableCell>{lead.contactName}</TableCell>
                                                <TableCell>{lead.columnName ?? '-'}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={lead.statusName}
                                                        variant="outlined"
                                                        sx={{ borderColor: lead.statusColor }}
                                                    />
                                                </TableCell>
                                                <TableCell>{lead.assignedUserName ?? '-'}</TableCell>
                                                <TableCell>{money(lead.value)}</TableCell>
                                                <TableCell>{t(OUTCOME_KEY[lead.outcome])}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <MDButton variant="gradient" color="dark" onClick={onClose}>
                    {t('general.fechar')}
                </MDButton>
                <MDButton
                    variant="gradient"
                    color="info"
                    disabled={!company}
                    onClick={() => company && onEdit(company.id)}
                >
                    {t('general.editar')}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};
